import { AxiosResponse } from 'axios';
import {
  call,
  fork,
  put,
  select,
  take,
  takeEvery,
  takeLatest,
} from 'redux-saga/effects';

import { transformDataDaiSettingForApi } from '../domain/dai/buildDataDaiApiDateParams';
import {
  DataDai,
  SettingsOptionsDai,
  UnitDataListFormConditions,
} from '../domain/dai/types';
import { Column, LoadingState } from '../domain/schemas';
import { ShuOption } from '../domain/shu';
import {
  DTMA_RANKING_STICKY_COLUMN_CODES,
  UnitDataListDataViewMode,
  buildDtmaRankingFetchParams,
} from '../domain/unitDataList/ranking';

import {
  ChangeDataDaiColumnsOrderAction,
  DataDaiActionCreators,
  DataDaiActionTypes,
  FetchDataDaiAction,
  dataDaiColumnsOrderSelector,
  dataDaiDataColumnsSelector,
  dataDaiOrderedDataSelector,
  dataDaiParamsSelector,
} from '../redux/server/dataDai';
import {
  SettingsOptionsDaiActionTypes,
  settingsOptionsDaiLoadingStateSelector,
  settingsOptionsDaiSelector,
} from '../redux/server/settingsOptionsDai';
import {
  UnitDataListReportsSettingActionCreators,
  selectUnitDataListReportsCurrentShuAction,
  unitDataListReportsDataViewModeSelector,
  unitDataListReportsSearchConditionSelector,
  unitDataListReportsSelectedCurrentShuSelector,
  unitDataListReportsSelectedDateRangeParamsSelector,
  unitDataListReportsTableHeaderSubMenuMultiFilterSelector,
} from '../redux/ui/unitDataListReportsSetting';
import { Api, buildConfig } from '../utils/api';
import { customSelect } from '../utils/customSelect';
import {
  STANDARD_STICKY_COLUMN_CODES,
  buildStickyFirstColumnsOrder,
  flattenColumns,
  recalcColumnOrder,
} from '../utils/orderedCell';
import { convertShuOption, existShuOption } from '../utils/shu';
import {
  collectUniqueColumnValues,
  selectedValuesWithinOptions,
  typeMultiFilterSelectOptions,
} from '../utils/tableHeaderSubMenuMultiFilter';
import { unitDataReportsDateUnits } from '../utils/unitDataListDateRange';
import { handleErrorSaga } from './errorSaga';
import { sisTypeNamesForTypeMultiFilterSaga } from './sisTypeNamesForTypeMultiFilterSaga';

/**
 * /data/dai 送信直前に日付パラメータを API 向けへ変換する。
 * Redux の searchCondition は ymdList を維持したまま、ここでのみ start/end 化して 414 を回避する。
 */
function* resolveDataDaiApiParams(params: DataDai['setting']) {
  const { dateUnit, isComparison } = yield* customSelect(
    unitDataListReportsSelectedDateRangeParamsSelector
  );

  return transformDataDaiSettingForApi(
    params,
    dateUnit ?? unitDataReportsDateUnits.at(0) ?? '日',
    isComparison
  );
}

/**
 * 台別データ一覧のデータを取得する
 * @param api AxiosInstance
 * @param action Action
 */
export function* fetchDataDaiSaga(api: Api, action: FetchDataDaiAction) {
  try {
    yield put(DataDaiActionCreators.fetchDataDaiRequestAction());
    // 出玉ランキング表示時は専用エンドポイント・専用パラメータで取得する
    const dataViewMode = yield* customSelect(
      unitDataListReportsDataViewModeSelector
    );
    const isRanking = dataViewMode === 'ranking';
    const path = isRanking ? '/data/dai/dtmaRanking' : '/data/dai';
    // 日付パラメータをAPI向けに変換する（長期間の ymdList による URL 肥大=414 を回避）
    const apiParams: DataDai['setting'] = yield* resolveDataDaiApiParams(
      action.payload.params
    );
    // 出玉ランキングは比較期間を除去し、デフォルトソートを差の昇順にする
    const params = isRanking
      ? buildDtmaRankingFetchParams(apiParams)
      : apiParams;
    const response: AxiosResponse<DataDai> = yield call(
      api.get,
      path,
      buildConfig(params)
    );
    yield put(DataDaiActionCreators.fetchDataDaiSuccessAction(response.data));
    // 出玉ランキングにはタイプ列の複数選択が無いため、標準表示の選択を残す
    if (!isRanking) {
      yield call(syncTypeMultiFilterWithDataDaiSaga, response.data);
    }
  } catch (error: unknown) {
    yield put(DataDaiActionCreators.renewDataDaiAction());
    yield fork(handleErrorSaga, error);
  }
}

/**
 * タイプ列の選択肢を取得結果から作り、選択肢に無い選択を捨てる。
 * タイプ列が無いときは選択肢も選択も空にする。
 */
export function* syncTypeMultiFilterWithDataDaiSaga(dataDai: DataDai) {
  const columns = flattenColumns(dataDai.data.columns);
  const hasTypeColumn = columns.some((column) => column.code === 'type');
  const sisTypeNames: string[] = hasTypeColumn
    ? yield call(sisTypeNamesForTypeMultiFilterSaga)
    : [];
  const uniqueTypeValues = collectUniqueColumnValues(
    [
      {
        columns,
        rows: dataDai.data.rows.filter(
          (row) => row.data.at(0)?.value !== '平均/合計'
        ),
      },
    ],
    'type'
  );
  const typeOptions = typeMultiFilterSelectOptions(
    sisTypeNames,
    uniqueTypeValues
  );

  yield put(
    UnitDataListReportsSettingActionCreators.selectUnitDataListReportsTableHeaderSubMenuMultiFilterSelectOptionsAction(
      { type: typeOptions }
    )
  );

  const currentFilter = yield* customSelect(
    unitDataListReportsTableHeaderSubMenuMultiFilterSelector
  );
  const selectedType = currentFilter.type ?? [];
  const nextType = selectedValuesWithinOptions(selectedType, typeOptions);
  if (selectedType.length !== nextType.length) {
    yield put(
      UnitDataListReportsSettingActionCreators.selectUnitDataListReportsTableHeaderSubMenuMultiFilterAction(
        { ...currentFilter, type: nextType }
      )
    );
  }
}

// ハイライト機種の平均合計行データを取得する
function* fetchDataDaiHighlightAverageSaga(
  api: Api,
  action: FetchDataDaiAction
) {
  try {
    const apiParams: DataDai['setting'] = yield* resolveDataDaiApiParams(
      action.payload.params
    );
    const response: AxiosResponse<DataDai> = yield call(
      api.get,
      '/data/dai',
      buildConfig({
        ...apiParams,
        containsAverage: true,
        containsData: false,
      })
    );
    const dataDai = response.data;

    yield put(
      DataDaiActionCreators.fetchDataDaiHighlightAverageSuccessAction(dataDai)
    );
  } catch (error: unknown) {
    yield put(DataDaiActionCreators.renewDataDaiAction());
    yield fork(handleErrorSaga, error);
  }
}

/**
 * 表示項目が変更されている場合並び替えに反映する
 */
function* columnsOrderCheckSaga() {
  const fields: Column[] = yield select(dataDaiDataColumnsSelector);

  const fieldCodes = fields.map((field) => field.code);

  // テーブル列の並び順
  const nowColumnsOrder: string[] | undefined = yield select(
    dataDaiColumnsOrderSelector
  );

  // 並び替え未設定時はデフォルトを使用
  const columnsOrder =
    !nowColumnsOrder || nowColumnsOrder.length === 0
      ? fieldCodes
      : nowColumnsOrder;

  // 固定列（標準：機種名・台番号の2列／出玉ランキング：日付・機種名・台番号の3列）は常に先頭。
  // APIレスポンスの列順（出玉ランキングは日付が先頭とは限らない）に依存しないよう、
  // 位置ではなく列コードで固定列を判定する。
  const dataViewMode: UnitDataListDataViewMode = yield select(
    unitDataListReportsDataViewModeSelector
  );
  const stickyColumnCodes =
    dataViewMode === 'ranking'
      ? DTMA_RANKING_STICKY_COLUMN_CODES
      : STANDARD_STICKY_COLUMN_CODES;

  const sorted = buildStickyFirstColumnsOrder(
    fieldCodes,
    columnsOrder,
    stickyColumnCodes
  );

  yield put(DataDaiActionCreators.selectDataDaiColumnsOrderAction(sorted));
}

/**
 * ドラッグ＆ドロップしたセルのIDを元に並び替え情報を登録する
 */
function* changeDataDaiColumnsOrderSaga(
  action: ChangeDataDaiColumnsOrderAction
) {
  const tableData: DataDai['data'] = yield select(dataDaiOrderedDataSelector);

  const ordered = recalcColumnOrder(
    tableData.columns,
    action.payload.draggedId,
    action.payload.droppedId
  );

  yield put(DataDaiActionCreators.selectDataDaiColumnsOrderAction(ordered));
}

/**
 * リクエストした種別のデータとレスポンスの種別データが不一致だったときに、レスポンスの値で上書きする
 *
 * 種別の不一致は種別が削除されたとき、あるいは共有グループをまたいだお気に入りで発生する
 */
function* validateShuSaga() {
  const currentShu: ShuOption = yield select(
    unitDataListReportsSelectedCurrentShuSelector
  );
  // currentShuが存在しない場合には初期リクエストになるため何もしない
  if (currentShu == null) {
    return;
  }

  // 種別全体はレスポンス setting と対応する項目がないため整合性検証をしない
  if (currentShu.type === 'none') {
    return;
  }

  const setting: DataDai['setting'] = yield select(dataDaiParamsSelector);
  const exist = existShuOption(setting, currentShu);
  if (exist) {
    return;
  }

  const settingsOptionsLoadingState: LoadingState = yield select(
    settingsOptionsDaiLoadingStateSelector
  );
  if (settingsOptionsLoadingState !== 'loaded') {
    yield take(
      SettingsOptionsDaiActionTypes.FETCH_SETTINGS_OPTIONS_DAI_SUCCESS
    );
  }
  // FETCH_SETTINGS_OPTIONS_KI_SUCCESSを待つのでundefinedにならない
  const settingsOptions: SettingsOptionsDai = yield select(
    settingsOptionsDaiSelector
  );

  const shuOption = convertShuOption(
    setting,
    settingsOptions.searchCondition.shuGroupList
  );
  if (shuOption == null) {
    throw new Error('適切な種別がsettingsOptionsで見つかりませんでした');
  }

  yield put(selectUnitDataListReportsCurrentShuAction(shuOption));
}

function* syncSortFromDataDaiResponseSaga() {
  const setting: DataDai['setting'] = yield select(dataDaiParamsSelector);
  const searchCondition: UnitDataListFormConditions = yield select(
    unitDataListReportsSearchConditionSelector
  );
  // 標準・出玉ランキングとも、サーバーが返した並び順を正とし、無ければ現在の検索条件
  // （ユーザー選択）を使う。出玉ランキングも sort/order を BE へ送るようになった
  // （buildDtmaRankingFetchParams）ため、サーバーは並び順を setting に載せて返し、
  // 標準と同一ロジックで整合できる。仮にサーバーが並び順を返さなくても、
  // searchCondition 側（フォーム引き継ぎ・reducerシードで常にユーザー選択を保持）を
  // フォールバックに使うため、検索条件変更・スライダ操作後もソートが維持される。
  const sort = setting?.sort ?? searchCondition.sort;
  const order = setting?.order ?? searchCondition.order;
  if (sort !== undefined && order !== undefined) {
    yield put(
      UnitDataListReportsSettingActionCreators.triggerUnitDataListReportsSortAction(
        sort,
        order
      )
    );
  }
}

function* handleFetchDaiSaga(api: Api) {
  // 表示区分の高速切替などで複数のフェッチが並走し、着順逆転で
  // 現在の表示区分と異なるデータが残るのを防ぐため takeLatest で最新のみ反映する
  yield takeLatest(DataDaiActionTypes.FETCH_DATA_DAI, fetchDataDaiSaga, api);
  // 表示項目と並び替えのチェック
  yield takeEvery(
    DataDaiActionTypes.FETCH_DATA_DAI_SUCCESS,
    columnsOrderCheckSaga
  );

  yield takeEvery(DataDaiActionTypes.FETCH_DATA_DAI_SUCCESS, validateShuSaga);
  yield takeEvery(
    DataDaiActionTypes.FETCH_DATA_DAI_SUCCESS,
    syncSortFromDataDaiResponseSaga
  );
}

function* handleChangeColumnsOrderSaga() {
  // 並び替え時
  yield takeEvery(
    DataDaiActionTypes.CHANGE_DATA_DAI_COLUMNS_ORDER,
    changeDataDaiColumnsOrderSaga
  );
}

function* handleFetchDataDaiHighlightAverageSaga(api: Api) {
  yield takeEvery(
    DataDaiActionTypes.FETCH_DATA_DAI_HIGHLIGHT_AVERAGE_REQUEST,
    fetchDataDaiHighlightAverageSaga,
    api
  );
}

export function* dataDaiSagas(context: { api: Api }) {
  yield fork(handleFetchDaiSaga, context.api);
  yield fork(handleChangeColumnsOrderSaga);
  yield fork(handleFetchDataDaiHighlightAverageSaga, context.api);
}
